import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal, Eyebrow } from "./Reveal";
import { ArrowRight } from "lucide-react";
import { productPath } from "@/lib/seo";
import { displayTitle } from "@/lib/title";
import { trackViewItemList, trackSelectItem } from "@/lib/analytics";
import { FeaturedProducts } from "./FeaturedProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

// Homepage merchandising rails (P1.14): sections are curated and edited in
// the admin panel (never random). Falls back to the legacy featured block
// only if no sections are configured.
export const MerchandisingRails = () => {
  const [sections, setSections] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/merchandising/sections`)
      .then((r) => {
        setSections(r.data.sections || []);
        (r.data.sections || []).forEach((s) => {
          if (s.items?.length) trackViewItemList(s.items, `Homepage: ${s.title}`);
        });
      })
      .catch(() => setSections([]));
  }, []);

  if (sections === null) return null;
  if (sections.length === 0) return <FeaturedProducts />;

  return (
    <div data-testid="merchandising-rails">
      {sections.slice(0, 4).map((s, si) => (
        <section
          key={s.id}
          data-testid={`merch-section-${si}`}
          className={`py-20 lg:py-28 border-b border-[#E8DCC4] ${si % 2 ? "bg-[#FDFBF7]" : "bg-white"}`}
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-xl">
                <Eyebrow>Gold Time Picks</Eyebrow>
                <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-neutral-900" data-testid={`merch-heading-${si}`}>
                  {s.title}
                </h2>
                {s.subtitle && <p className="mt-3 text-sm text-neutral-500">{s.subtitle}</p>}
              </div>
              {s.link && (
                <Link
                  to={s.link}
                  data-testid={`merch-browse-${si}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
                >
                  Browse all <ArrowRight size={16} strokeWidth={1.5} />
                </Link>
              )}
            </Reveal>
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
              {s.items.map((p, i) => (
                <Reveal key={p.sku} delay={0.05 * i}>
                  <Link
                    to={productPath(p)}
                    onClick={() => trackSelectItem(p, `Homepage: ${s.title}`)}
                    data-testid={`merch-card-${p.sku}`}
                    className="group block bg-white border border-[#E8DCC4] rounded-sm overflow-hidden hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-500"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={p.image_url}
                        alt={displayTitle(p)}
                        loading="lazy"
                        className="w-full h-[160px] sm:h-[200px] lg:h-[220px] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-neutral-900 leading-snug line-clamp-2">
                        {displayTitle(p)}
                      </h3>
                      <p className="text-sm font-semibold text-[#A8871B] mt-2">{money(p.retail_price)}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};
